// Shared comment UI primitives used by both the sidebar list view and the
// per-pin floating popover. Lifted out of CommentSidebar so the popover
// can reuse the exact same composer + thread rendering — keep them in
// sync by editing here once instead of twice.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import type {
  ApiComment,
  ApiCommentPosition,
  CreateCommentInput,
} from "../lib/api";
import { readReviewer, writeReviewer } from "../lib/reviewerIdentity";
import { DeviceIcon, deviceFromViewport } from "./DeviceIcon";

export type OwnerActions = {
  onResolve: (commentId: string) => Promise<void>;
  onReopen: (commentId: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
};

export function CommentCard({
  comment,
  compact = false,
  editing = false,
  editable = false,
  tourReplyAnchor = false,
  tourEditAnchor = false,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onResolve,
  onReopen,
  onDelete,
  onReply,
  onFocusPin,
}: {
  comment: ApiComment;
  compact?: boolean;
  editing?: boolean;
  editable?: boolean;
  tourReplyAnchor?: boolean;
  tourEditAnchor?: boolean;
  onStartEdit?: () => void;
  onCancelEdit?: () => void;
  onSaveEdit?: (next: string) => Promise<void>;
  onResolve?: (id: string) => Promise<void>;
  onReopen?: (id: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onReply?: () => void;
  onFocusPin?: () => void;
}) {
  return (
    <div>
      <div className="flex items-start gap-2">
        <Avatar name={comment.authorName} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-neutral-900">{comment.authorName}</span>
            <span className="inline-flex items-baseline gap-1 text-xs text-neutral-400">
              {formatTime(comment.createdAt)}
              <DeviceIcon
                device={deviceFromViewport(comment.position.viewportWidth)}
                className="translate-y-0.5"
              />
            </span>
          </div>
          {editing && onSaveEdit && onCancelEdit ? (
            <EditField
              initial={comment.body}
              onSave={onSaveEdit}
              onCancel={onCancelEdit}
            />
          ) : (
            <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-neutral-700">
              {comment.body}
            </p>
          )}
        </div>
      </div>
      {!editing ? (
        <div className="mt-2 ml-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
          {!compact && onFocusPin ? (
            <button
              type="button"
              onClick={onFocusPin}
              className="whitespace-nowrap hover:text-neutral-900"
            >
              Find
            </button>
          ) : null}
          {!compact && onReply ? (
            <button
              type="button"
              onClick={onReply}
              {...(tourReplyAnchor ? { "data-tour-id": "comment-reply" } : {})}
              className="whitespace-nowrap hover:text-neutral-900"
            >
              Reply
            </button>
          ) : null}
          {editable && onStartEdit ? (
            <button
              type="button"
              onClick={onStartEdit}
              {...(tourEditAnchor ? { "data-tour-id": "comment-edit" } : {})}
              className="whitespace-nowrap hover:text-neutral-900"
            >
              Edit
            </button>
          ) : null}
          {!compact && comment.status === "open" && onResolve ? (
            <button
              type="button"
              onClick={() => void onResolve(comment.id)}
              className="whitespace-nowrap hover:text-neutral-900"
            >
              Resolve
            </button>
          ) : null}
          {!compact && comment.status === "resolved" && onReopen ? (
            <button
              type="button"
              onClick={() => void onReopen(comment.id)}
              className="whitespace-nowrap hover:text-neutral-900"
            >
              Reopen
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    compact
                      ? "Delete this reply?"
                      : "Delete this comment and all of its replies?",
                  )
                ) {
                  void onDelete(comment.id);
                }
              }}
              className="whitespace-nowrap text-red-600 hover:text-red-700"
            >
              Delete
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function EditField({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (next: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
    const len = ref.current?.value.length ?? 0;
    ref.current?.setSelectionRange(len, len);
  }, []);

  async function commit() {
    const next = value.trim();
    if (!next) return;
    if (next === initial) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 403) setError("You can only edit your own comments.");
      else setError("Couldn’t save the edit.");
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void commit();
    }
  }

  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKey}
        rows={3}
        maxLength={4000}
        disabled={busy}
        className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:border-neutral-900 focus:outline-none disabled:bg-neutral-50"
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-xs text-neutral-500 hover:text-neutral-900 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void commit()}
          disabled={busy || !value.trim()}
          className="rounded-md bg-neutral-900 px-3 py-0.5 text-xs text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-semibold text-white">
      {initials(name)}
    </span>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 1).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function formatTime(ts: number): string {
  const d = new Date(ts);
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
  return d.toLocaleDateString();
}

export function Composer({
  pageUrl,
  position,
  parentId,
  defaultIdentity,
  onCreate,
  onCancel,
  kind,
  variant = "sidebar",
}: {
  pageUrl: string;
  position: ApiCommentPosition;
  parentId?: string;
  defaultIdentity: { name: string; email: string } | null;
  onCreate: (input: CreateCommentInput) => Promise<ApiComment>;
  onCancel: () => void;
  kind: "root" | "reply";
  // "sidebar" keeps the original card framing (top banner + neutral bg).
  // "popover" drops the framing chrome since the popover already has its
  // own border/shadow and bubble pointer; redundant chrome looks heavy.
  variant?: "sidebar" | "popover";
}) {
  const cached = !defaultIdentity ? readReviewer() : null;
  const [name, setName] = useState(defaultIdentity?.name ?? cached?.name ?? "");
  const [email, setEmail] = useState(defaultIdentity?.email ?? cached?.email ?? "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lockedIdentity = !!defaultIdentity;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onCreate({
        pageUrl,
        parentId: parentId ?? null,
        authorName: name.trim(),
        authorEmail: email.trim(),
        body: body.trim(),
        position,
      });
      if (!lockedIdentity) writeReviewer({ name: name.trim(), email: email.trim() });
      setBody("");
      if (kind === "reply") onCancel();
    } catch (err) {
      setError((err as Error).message || "Couldn’t post that comment.");
    } finally {
      setBusy(false);
    }
  }

  const frame =
    variant === "popover"
      ? "p-3"
      : kind === "root"
        ? "border-b border-neutral-200 bg-neutral-50/60 px-5 py-4"
        : "rounded-md bg-neutral-50 p-3";

  return (
    <form onSubmit={onSubmit} className={frame}>
      {kind === "root" && variant === "sidebar" ? (
        <div className="mb-2 flex items-center gap-2 text-xs text-neutral-500">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-semibold text-white">
            📍
          </span>
          Comment pinned to the spot you clicked.
        </div>
      ) : null}
      {!lockedIdentity ? (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            required
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:border-neutral-900 focus:outline-none"
          />
          <input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:border-neutral-900 focus:outline-none"
          />
        </div>
      ) : null}
      <textarea
        required
        autoFocus={variant === "popover"}
        rows={kind === "reply" ? 2 : 3}
        placeholder={kind === "reply" ? "Reply…" : "Leave a comment…"}
        maxLength={4000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="mt-2 w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:border-neutral-900 focus:outline-none"
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2.5 py-1 text-xs text-neutral-500 hover:text-neutral-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !body.trim()}
          className="rounded-md bg-neutral-900 px-3 py-1 text-xs text-white hover:bg-neutral-700 disabled:opacity-50"
        >
          {busy ? "Sending…" : kind === "reply" ? "Reply" : "Comment"}
        </button>
      </div>
    </form>
  );
}
